import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // CRITICAL: Required for httpOnly cookie auth (Issue #289)
});

/**
 * Helper function to get CSRF token from cookies
 * CSRF token is stored in a readable cookie (not httpOnly) for client-side access
 *
 * Note: CSRF token is URL-safe (generated with secrets.token_urlsafe) and does not
 * require decoding. Decoding the entire cookie string could break parsing if other
 * cookies contain encoded semicolons.
 */
function getCsrfToken(): string | null {
  const name = 'csrf_token=';
  const cookies = document.cookie.split(';');

  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith(name)) {
      return cookie.substring(name.length);
    }
  }
  return null;
}

// Request interceptor to add CSRF token header
apiClient.interceptors.request.use((config) => {
  // Add CSRF token to header for CSRF protection (Issue #289)
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken;
  }

  return config;
});

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired, try to refresh using httpOnly cookies
      try {
        // Refresh endpoint reads refresh_token from httpOnly cookie
        await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {}, {
          withCredentials: true, // Send cookies with refresh request
        });

        // Refresh successful, new access_token is now in httpOnly cookie
        // Retry the original request
        return apiClient.request(error.config);
      } catch (refreshError) {
        // Refresh failed. Only bounce to /login from an authenticated area —
        // not when already on a public/auth page. Otherwise the initial /me
        // auth-check that runs on every page load would redirect a logged-out
        // visitor away from /register (and other public pages) to /login.
        const publicPaths = [
          '/login',
          '/register',
          '/download',
          '/forgot-password',
          '/reset-password',
          '/verify-email',
          '/email-verification-pending',
          '/auth',
          '/device-login',
          '/device-authorized',
          '/join',
        ];
        const path = window.location.pathname;
        // Match the exact path or a sub-path (`/auth/github/callback`), but not
        // an unrelated route that merely shares a prefix (`/login-settings`).
        const onPublicPage = publicPaths.some(
          (base) => path === base || path.startsWith(`${base}/`)
        );
        if (!onPublicPage) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;